import { Injectable, computed, inject } from '@angular/core';
import { Either } from 'purify-ts';
import { Subscription } from 'rxjs';
import { ConversationsRepositoryFailure } from '../../domain/conversations/failure';
import { IConversationsRepository } from '../../domain/conversations/i-conversations-repository';
import {
  ContactName,
  Conversation,
  ConversationWindowStatus,
  Message,
  MessagePatch,
  SendMessageProps,
  SentMessage,
  buildMessagePreview,
  buildPendingMessage,
  chatMessages,
  conversationWindowStatus,
  draftOfSendProps,
  isWindowOpenFromMessages,
  mediaMessageTypeOf,
  unreadCountOf,
  windowCloseAtFromMessages,
} from '../../domain/conversations/types';
import { IEventsRepository } from '../../domain/events/i-events-repository';
import { WhatsappEvent } from '../../domain/events/types';
import { IToastController } from '../../domain/global/i-toast-controller';
import { IMediaRepository } from '../../domain/media/i-media-repository';
import {
  MEDIA_TOO_LARGE_MESSAGE,
  MEDIA_TYPE_NOT_ALLOWED_MESSAGE,
  isAllowedMediaType,
  isWithinMediaSizeLimit,
} from '../../domain/media/types';
import { IPhoneNumbersRepository } from '../../domain/phone-numbers/i-phone-numbers-repository';
import { IStorageRepository } from '../../domain/storage';
import { StoreService } from '../store.service';
import { WhatsappState } from './type';
import { leftValue, rightValue } from '../../util/either';

const CONVERSATIONS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 30;

const READ_MARK_DEBOUNCE_MS = 600;

interface MediaUpload {
  file: File;
  type: 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO';
  caption: string;
}

type RetryPayload =
  | { kind: 'SEND'; props: SendMessageProps }
  | { kind: 'MEDIA'; companyPhoneNumber: string; clientPhoneNumber: string; upload: MediaUpload };

@Injectable({
  providedIn: 'root',
})
export class WhatsappSignalsStateService extends StoreService<WhatsappState> {
  private phoneNumbersRepository = inject(IPhoneNumbersRepository);
  private conversationsRepository = inject(IConversationsRepository);
  private eventsRepository = inject(IEventsRepository);
  private mediaRepository = inject(IMediaRepository);
  private storage = inject(IStorageRepository);
  private toastController = inject(IToastController);

  private eventsSubscription: Subscription | null = null;

  private connectedCompanyPhoneNumber = '';

  private readMarkTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly retryPayloads = new Map<string, RetryPayload>();

  private readonly onVisibilityChange = (): void => {
    if (this.isPageVisible()) {
      this.markActiveConversationAsRead();
    }
  };

  readonly activeNumber = computed(() =>
    this.state().numbers.find(
      number => number.companyPhoneNumber === this.state().activeCompanyPhoneNumber
    )
  );

  readonly hasActiveNumber = computed(() => !!this.state().activeCompanyPhoneNumber);

  readonly activeNumberLabel = computed(
    () => this.activeNumber()?.verifiedName ?? 'Selecionar número'
  );

  readonly activeConversation = computed(() =>
    this.state().conversations.find(
      conversation => conversation.phoneNumber === this.state().activeClientPhoneNumber
    )
  );

  readonly activeContactName = computed<ContactName | undefined>(() => {
    const phoneNumber = this.state().activeClientPhoneNumber;
    const stored = this.state().contactNames[phoneNumber];

    if (stored) {
      return stored;
    }

    const conversation = this.activeConversation();

    return conversation
      ? {
          phoneNumber,
          name: conversation.name,
          chosenName: conversation.chosenName ?? null,
        }
      : undefined;
  });

  readonly messages = computed(() => chatMessages(this.state().messages));

  readonly isWindowOpen = computed(() => isWindowOpenFromMessages(this.state().messages));

  readonly windowCloseAt = computed(() => windowCloseAtFromMessages(this.state().messages));

  readonly hasMoreConversations = computed(() => this.state().conversationsCursor !== null);

  readonly hasMoreMessages = computed(() => this.state().messagesCursor !== null);

  protected getInitialState(): WhatsappState {
    return {
      numbers: [],
      numbersLoading: false,
      activeCompanyPhoneNumber: '',
      conversations: [],
      conversationsCursor: null,
      conversationsLoading: false,
      contactNames: {},
      activeClientPhoneNumber: '',
      messages: [],
      messagesCursor: null,
      messagesLoading: false,
    };
  }

  public conversationWindow(conversation: Conversation): ConversationWindowStatus {
    return conversationWindowStatus(conversation);
  }

  private unreadOf(phoneNumber: string): number {
    return unreadCountOf(
      this.state().conversations.find(conversation => conversation.phoneNumber === phoneNumber)
    );
  }

  public async loadNumbers(): Promise<void> {
    this.set('numbersLoading', true);
    const result = await this.phoneNumbersRepository.getList();
    this.set('numbersLoading', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return;
    }

    const numbers = rightValue(result);
    this.set('numbers', numbers);

    const stored = this.storage.getStorage('activeCompanyPhoneNumber');
    const storedNumber = stored.isRight() ? rightValue(stored) : '';
    const isStillValid = numbers.some(number => number.companyPhoneNumber === storedNumber);

    if (isStillValid && storedNumber !== this.state().activeCompanyPhoneNumber) {
      await this.selectNumber(storedNumber);
    }
  }

  public async selectNumber(companyPhoneNumber: string): Promise<void> {
    this.storage.setStorage('activeCompanyPhoneNumber', companyPhoneNumber);
    this.retryPayloads.clear();
    this.setState({
      activeCompanyPhoneNumber: companyPhoneNumber,
      conversations: [],
      conversationsCursor: null,
      contactNames: {},
      activeClientPhoneNumber: '',
      messages: [],
      messagesCursor: null,
    });

    this.connectEvents();

    await this.loadConversations(true);
  }

  public async loadConversations(reset = false): Promise<void> {
    const companyPhoneNumber = this.state().activeCompanyPhoneNumber;

    if (!companyPhoneNumber || this.state().conversationsLoading) {
      return;
    }

    const cursor = reset ? null : this.state().conversationsCursor;

    if (!reset && !cursor && this.state().conversations.length > 0) {
      return;
    }

    this.set('conversationsLoading', true);
    const result = await this.conversationsRepository.getConversations({
      companyPhoneNumber,
      cursor,
      limit: CONVERSATIONS_PAGE_SIZE,
    });
    this.set('conversationsLoading', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return;
    }

    const page = rightValue(result);

    const known = new Set(reset ? [] : this.state().conversations.map(item => item.phoneNumber));
    const fresh = page.items.filter(conversation => !known.has(conversation.phoneNumber));

    this.setState({
      conversations: reset ? page.items : [...this.state().conversations, ...fresh],
      conversationsCursor: page.nextCursor,
    });
  }

  public async selectConversation(clientPhoneNumber: string): Promise<void> {
    this.retryPayloads.clear();
    this.setState({
      activeClientPhoneNumber: clientPhoneNumber,
      messages: [],
      messagesCursor: null,
    });

    this.clearUnreadOf(clientPhoneNumber);
    this.markConversationAsRead(clientPhoneNumber);

    await this.loadMessages(true);
  }

  public async loadMessages(reset = false): Promise<void> {
    const { activeCompanyPhoneNumber, activeClientPhoneNumber } = this.state();

    if (!activeCompanyPhoneNumber || !activeClientPhoneNumber || this.state().messagesLoading) {
      return;
    }

    const cursor = reset ? null : this.state().messagesCursor;

    if (!reset && !cursor && this.state().messages.length > 0) {
      return;
    }

    this.set('messagesLoading', true);
    const result = await this.conversationsRepository.getMessages({
      companyPhoneNumber: activeCompanyPhoneNumber,
      clientPhoneNumber: activeClientPhoneNumber,
      cursor,
      limit: MESSAGES_PAGE_SIZE,
    });
    this.set('messagesLoading', false);

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return;
    }

    const page = rightValue(result);
    const olderFirst = [...page.items].reverse();

    const known = new Set(reset ? [] : this.state().messages.map(message => message.id));
    const fresh = olderFirst.filter(message => !known.has(message.id));

    this.setState({
      messages: reset ? olderFirst : [...fresh, ...this.state().messages],
      messagesCursor: page.nextCursor,
    });
  }

  public async sendMessage(props: SendMessageProps): Promise<boolean> {
    const pending = buildPendingMessage(draftOfSendProps(props));
    this.appendPendingMessage(props.clientPhoneNumber, pending);
    this.retryPayloads.set(pending.id, { kind: 'SEND', props });

    return this.dispatchSend(pending.id, props);
  }

  public async retryMessage(messageId: string): Promise<boolean> {
    const payload = this.retryPayloads.get(messageId);

    if (!payload) {
      return false;
    }

    this.markMessageAsPending(messageId);

    if (payload.kind === 'MEDIA') {
      return this.uploadAndSend(
        payload.companyPhoneNumber,
        payload.clientPhoneNumber,
        payload.upload,
        messageId
      );
    }

    return this.dispatchSend(messageId, payload.props);
  }

  private async dispatchSend(pendingId: string, props: SendMessageProps): Promise<boolean> {
    const result = await this.conversationsRepository.sendMessage(props);

    return this.settlePendingMessage(pendingId, result);
  }

  public async sendMediaFiles(files: File[], caption = ''): Promise<boolean> {
    const {
      activeCompanyPhoneNumber: companyPhoneNumber,
      activeClientPhoneNumber: clientPhoneNumber,
    } = this.state();

    if (!companyPhoneNumber || !clientPhoneNumber) {
      return false;
    }

    const accepted = files.filter(file => this.isSendableMedia(file));

    if (accepted.length === 0) {
      return false;
    }

    const uploads = accepted.map((file, index) => {
      const type = mediaMessageTypeOf(file.type);
      const mediaCaption = index === 0 && type !== 'AUDIO' ? caption : '';
      const upload: MediaUpload = { file, type, caption: mediaCaption };
      const pending = buildPendingMessage({
        type,
        content: { media: { mediaId: '', fileName: file.name, caption: mediaCaption } },
      });

      this.appendPendingMessage(clientPhoneNumber, pending);
      this.retryPayloads.set(pending.id, {
        kind: 'MEDIA',
        companyPhoneNumber,
        clientPhoneNumber,
        upload,
      });

      return { upload, pendingId: pending.id };
    });

    let allSent = true;

    for (const { upload, pendingId } of uploads) {
      const sent = await this.uploadAndSend(
        companyPhoneNumber,
        clientPhoneNumber,
        upload,
        pendingId
      );
      allSent = allSent && sent;
    }

    return allSent;
  }

  private async uploadAndSend(
    companyPhoneNumber: string,
    clientPhoneNumber: string,
    upload: MediaUpload,
    pendingId: string
  ): Promise<boolean> {
    const uploaded = await this.mediaRepository.upload(companyPhoneNumber, upload.file);

    if (uploaded.isLeft()) {
      const errorMessage = leftValue(uploaded).errorMessage;
      this.toastController.showErrorMessage(errorMessage);
      this.failPendingMessage(pendingId, errorMessage);
      return false;
    }

    const media = rightValue(uploaded);

    return this.dispatchSend(pendingId, {
      companyPhoneNumber,
      clientPhoneNumber,
      type: upload.type,
      content: {
        mediaId: media.mediaId,
        fileName: media.fileName,
        ...(upload.caption ? { caption: upload.caption } : {}),
      },
    });
  }

  public isSendableMedia(file: File): boolean {
    if (!isAllowedMediaType(file.type)) {
      this.toastController.showErrorMessage(`${file.name}: ${MEDIA_TYPE_NOT_ALLOWED_MESSAGE}`);
      return false;
    }

    if (!isWithinMediaSizeLimit(file.size)) {
      this.toastController.showErrorMessage(`${file.name}: ${MEDIA_TOO_LARGE_MESSAGE}`);
      return false;
    }

    return true;
  }

  private appendPendingMessage(clientPhoneNumber: string, message: Message): void {
    if (clientPhoneNumber === this.state().activeClientPhoneNumber) {
      this.set('messages', [...this.state().messages, message]);
    }

    this.upsertConversation(clientPhoneNumber, message);
  }

  private settlePendingMessage(
    pendingId: string,
    result: Either<ConversationsRepositoryFailure, SentMessage>
  ): boolean {
    if (result.isLeft()) {
      const errorMessage = leftValue(result).errorMessage;
      this.toastController.showErrorMessage(errorMessage);
      this.failPendingMessage(pendingId, errorMessage);
      return false;
    }

    this.confirmPendingMessage(pendingId, rightValue(result));
    return true;
  }

  private confirmPendingMessage(pendingId: string, sent: SentMessage): void {
    this.retryPayloads.delete(pendingId);
    const messages = this.state().messages;

    if (!messages.some(message => message.id === pendingId)) {
      return;
    }

    const alreadyEchoed = messages.some(message => message.id === sent.id);

    this.set(
      'messages',
      alreadyEchoed
        ? messages.filter(message => message.id !== pendingId)
        : messages.map(message =>
            message.id === pendingId
              ? {
                  ...message,
                  id: sent.id,
                  wamid: sent.wamid,
                  status: sent.status,
                  failReason: null,
                  pending: false,
                  retryable: false,
                }
              : message
          )
    );
  }

  private failPendingMessage(pendingId: string, failReason: string): void {
    this.set(
      'messages',
      this.state().messages.map(message =>
        message.id === pendingId
          ? {
              ...message,
              status: 'FAILED' as const,
              failReason,
              pending: false,
              retryable: this.retryPayloads.has(pendingId),
            }
          : message
      )
    );
  }

  private markMessageAsPending(pendingId: string): void {
    this.set(
      'messages',
      this.state().messages.map(message =>
        message.id === pendingId
          ? { ...message, status: undefined, failReason: null, pending: true, retryable: false }
          : message
      )
    );
  }

  public connectEvents(): void {
    const companyPhoneNumber = this.state().activeCompanyPhoneNumber;

    if (!companyPhoneNumber) {
      this.disconnectEvents();
      return;
    }

    if (this.eventsSubscription && this.connectedCompanyPhoneNumber === companyPhoneNumber) {
      return;
    }

    this.disconnectEvents();

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.connectedCompanyPhoneNumber = companyPhoneNumber;

    this.eventsSubscription = this.eventsRepository.connect(companyPhoneNumber).subscribe({
      next: event => this.handleEvent(event),
      error: () => {
        this.eventsSubscription = null;
        this.connectedCompanyPhoneNumber = '';
      },
    });
  }

  public disconnectEvents(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.readMarkTimer) {
      clearTimeout(this.readMarkTimer);
      this.readMarkTimer = null;
    }

    this.eventsSubscription?.unsubscribe();
    this.eventsSubscription = null;
    this.connectedCompanyPhoneNumber = '';
  }

  public handleEvent(event: WhatsappEvent): void {
    if (event.companyPhoneNumber !== this.state().activeCompanyPhoneNumber) {
      return;
    }

    if (event.event === 'MESSAGE_STATUS_UPDATED') {
      this.applyStatusUpdate(event.clientPhoneNumber, event.message);
      return;
    }

    if (event.event === 'MESSAGE_REACTION_UPDATED') {
      this.applyReactionUpdate(event.clientPhoneNumber, event.message);
      return;
    }

    if (event.event === 'CONVERSATION_READ') {
      this.clearUnreadOf(event.clientPhoneNumber);
      return;
    }

    this.applyNewMessage(event.clientPhoneNumber, event.message);
  }

  private applyReactionUpdate(clientPhoneNumber: string, message: MessagePatch): void {
    if (clientPhoneNumber !== this.state().activeClientPhoneNumber) {
      return;
    }

    this.set(
      'messages',
      this.state().messages.map(current =>
        current.id === message.id ? { ...current, reactions: message.reactions ?? [] } : current
      )
    );
  }

  private applyStatusUpdate(clientPhoneNumber: string, message: MessagePatch): void {
    if (clientPhoneNumber !== this.state().activeClientPhoneNumber) {
      return;
    }

    this.set(
      'messages',
      this.state().messages.map(current =>
        current.id === message.id ? { ...current, ...message } : current
      )
    );
  }

  private applyNewMessage(clientPhoneNumber: string, message: Message): void {
    const isActive = clientPhoneNumber === this.state().activeClientPhoneNumber;

    if (isActive) {
      const messages = this.state().messages;
      const known = messages.some(current => current.id === message.id);

      this.set(
        'messages',
        known
          ? messages.map(current =>
              current.id === message.id ? { ...current, ...message, pending: false } : current
            )
          : [...messages, message]
      );
    }

    const isRead = isActive && this.isPageVisible();

    this.upsertConversation(clientPhoneNumber, message, message.direction === 'INBOUND' && !isRead);

    if (isRead && message.direction === 'INBOUND') {
      this.scheduleReadMark(clientPhoneNumber);
    }
  }

  public async updateContactName(clientPhoneNumber: string, chosenName: string): Promise<boolean> {
    const result = await this.conversationsRepository.updateContactName({
      clientPhoneNumber,
      chosenName,
    });

    if (result.isLeft()) {
      this.toastController.showErrorMessage(leftValue(result).errorMessage);
      return false;
    }

    const contact = rightValue(result);

    this.setState({
      contactNames: { ...this.state().contactNames, [clientPhoneNumber]: contact },
      conversations: this.state().conversations.map(conversation =>
        conversation.phoneNumber === clientPhoneNumber
          ? { ...conversation, name: contact.name, chosenName: contact.chosenName }
          : conversation
      ),
    });

    this.toastController.showSuccessMessage('Nome do contato atualizado.');
    return true;
  }

  private clearUnreadOf(clientPhoneNumber: string): void {
    if (this.unreadOf(clientPhoneNumber) === 0) {
      return;
    }

    this.set(
      'conversations',
      this.state().conversations.map(conversation =>
        conversation.phoneNumber === clientPhoneNumber
          ? { ...conversation, unreadCount: 0 }
          : conversation
      )
    );
  }

  private markConversationAsRead(clientPhoneNumber: string): void {
    const companyPhoneNumber = this.state().activeCompanyPhoneNumber;

    if (!companyPhoneNumber || !clientPhoneNumber) {
      return;
    }

    void this.conversationsRepository.markConversationAsRead({
      companyPhoneNumber,
      clientPhoneNumber,
    });
  }

  private scheduleReadMark(clientPhoneNumber: string): void {
    if (!this.isPageVisible()) {
      return;
    }

    if (this.readMarkTimer) {
      clearTimeout(this.readMarkTimer);
    }

    this.readMarkTimer = setTimeout(() => {
      this.readMarkTimer = null;

      if (this.state().activeClientPhoneNumber === clientPhoneNumber) {
        this.markConversationAsRead(clientPhoneNumber);
      }
    }, READ_MARK_DEBOUNCE_MS);
  }

  private markActiveConversationAsRead(): void {
    const clientPhoneNumber = this.state().activeClientPhoneNumber;

    if (!clientPhoneNumber) {
      return;
    }

    this.clearUnreadOf(clientPhoneNumber);
    this.markConversationAsRead(clientPhoneNumber);
  }

  private isPageVisible(): boolean {
    return document.visibilityState === 'visible';
  }

  private upsertConversation(
    clientPhoneNumber: string,
    message: Message,
    isNewUnread = false
  ): void {
    const conversations = this.state().conversations;
    const existing = conversations.find(
      conversation => conversation.phoneNumber === clientPhoneNumber
    );

    const closeAt = windowCloseAtFromMessages([message]);
    const knownContact = this.state().contactNames[clientPhoneNumber];

    const updated: Conversation = {
      phoneNumber: clientPhoneNumber,
      name: existing?.name ?? knownContact?.name ?? null,
      chosenName: existing?.chosenName ?? knownContact?.chosenName ?? null,
      lastMessageAt: message.messageAt,
      lastMessagePreview: buildMessagePreview(message),
      lastMessageDirection: message.direction,
      lastInboundAt:
        message.direction === 'INBOUND' ? message.messageAt : (existing?.lastInboundAt ?? null),
      messageWindowCloseAt: closeAt ? closeAt.toISOString() : existing?.messageWindowCloseAt,
      unreadCount: unreadCountOf(existing) + (isNewUnread ? 1 : 0),
    };

    this.set('conversations', [
      updated,
      ...conversations.filter(conversation => conversation.phoneNumber !== clientPhoneNumber),
    ]);
  }
}
